/**
 * The network half of the ORCID Publication List Generator.
 *
 * Works with a DOI are resolved through CitationEasy's citation service, ten DOIs to a
 * request (`POST /api/v1/cite` with `inputs`), so they carry the registry's full
 * metadata — every author, volume, issue, pages. Works without one, or whose DOI did
 * not resolve, are built from ORCID itself: their full records are fetched a hundred at
 * a time for the contributor list and any BibTeX the source attached. Every entry is
 * then formatted in one style through `/api/cite`'s `entries` batch.
 */

import type { EditableEntry } from "@/lib/citation";
import {
  chunked,
  type FetchFailure,
  fetchJson,
  runPool,
} from "@/lib/tool-kit/reference-tools/network";
import {
  ORCID_BULK_LIMIT,
  ORCID_HEADERS,
  type OrcidWork,
  orcidBulkUrl,
  orcidPersonUrl,
  orcidWorksUrl,
  parseOrcidBulk,
  parseOrcidName,
  parseOrcidWorks,
  workToEntry,
} from "@/lib/tool-kit/reference-tools/orcid";

/** DOIs per `/api/v1/cite` request: the service's own batch limit. */
export const CITE_BATCH = 10;

export interface OrcidProfile {
  id: string;
  owner: string;
  works: OrcidWork[];
}

export type ProfileResult =
  | { ok: true; profile: OrcidProfile }
  | { ok: false; reason: FetchFailure };

export async function fetchProfile(
  id: string,
  signal: AbortSignal
): Promise<ProfileResult> {
  const init = { headers: ORCID_HEADERS };
  const [person, works] = await Promise.all([
    fetchJson<unknown>(orcidPersonUrl(id), { signal, init }),
    fetchJson<unknown>(orcidWorksUrl(id), { signal, init }),
  ]);
  if (!works.ok) {
    return { ok: false, reason: works.reason };
  }
  return {
    ok: true,
    profile: {
      id,
      owner: person.ok ? parseOrcidName(person.value) : "",
      works: parseOrcidWorks(works.value),
    },
  };
}

export interface WorkSource {
  entry: EditableEntry;
  /** "doi": from the registry record; "orcid": built from ORCID's own metadata. */
  from: "doi" | "orcid";
  /** ORCID lists nobody for this work, so only the profile's owner was put in. */
  ownerOnly: boolean;
}

interface CiteResult {
  input?: string | null;
  entry?: EditableEntry | null;
  error?: string;
}

async function resolveDois(
  dois: string[],
  signal: AbortSignal
): Promise<Map<string, EditableEntry>> {
  const response = await fetchJson<{ results?: CiteResult[] }>("/api/v1/cite", {
    signal,
    init: {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ inputs: dois, style: "apa" }),
    },
    timeoutMs: 60_000,
  });
  const found = new Map<string, EditableEntry>();
  if (!response.ok) {
    return found;
  }
  for (const [index, result] of (response.value.results ?? []).entries()) {
    if (result.entry && !result.error) {
      found.set(dois[index], result.entry);
    }
  }
  return found;
}

export interface SourceProgress {
  done: number;
  total: number;
}

/**
 * An entry for every work: DOI works through the citation service, the rest (and any
 * DOI that failed) from ORCID's full records. `known` holds entries from an earlier
 * run, which are reused rather than fetched again.
 */
export async function buildSources(
  profile: OrcidProfile,
  works: readonly OrcidWork[],
  known: ReadonlyMap<number, WorkSource>,
  signal: AbortSignal,
  onProgress: (progress: SourceProgress) => void
): Promise<Map<number, WorkSource> | null> {
  const sources = new Map(known);
  const pending = works.filter((work) => !sources.has(work.putCode));
  const doiBatches = chunked(
    pending.filter((work) => work.doi !== null),
    CITE_BATCH
  );
  let done = 0;
  const withoutDoi = pending.filter((work) => work.doi === null).length;
  let total = doiBatches.length + Math.ceil(withoutDoi / ORCID_BULK_LIMIT);
  const resolved = await runPool(
    doiBatches,
    async (batch) => {
      const found = await resolveDois(
        batch.map((work) => work.doi as string),
        signal
      );
      done += 1;
      onProgress({ done, total });
      return batch.flatMap((work) => {
        const entry = found.get(work.doi as string);
        return entry ? [[work.putCode, entry] as const] : [];
      });
    },
    { concurrency: 2, signal }
  );
  if (resolved === null) {
    return null;
  }
  for (const [putCode, entry] of resolved.flat()) {
    sources.set(putCode, { entry, from: "doi", ownerOnly: false });
  }
  const remaining = pending.filter((work) => !sources.has(work.putCode));
  const bulkBatches = chunked(remaining, ORCID_BULK_LIMIT);
  total = doiBatches.length + bulkBatches.length;
  onProgress({ done, total });
  for (const batch of bulkBatches) {
    const response = await fetchJson<unknown>(
      orcidBulkUrl(
        profile.id,
        batch.map((work) => work.putCode)
      ),
      { signal, init: { headers: ORCID_HEADERS } }
    );
    const details = response.ok ? parseOrcidBulk(response.value) : new Map();
    for (const work of batch) {
      const built = workToEntry(work, details.get(work.putCode), profile.owner);
      sources.set(work.putCode, { ...built, from: "orcid" });
    }
    done += 1;
    onProgress({ done, total });
  }
  return sources;
}
